import { Link, Stack, Typography } from "@mui/material";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import type { SupportFlowStep } from "Components/Support/SupportFlowStep";
import type { SupportReportType } from "Components/Support/SupportReportType";
import {
  CONTENT_REPORT_FORM_ID,
  SENTRY_REPORT_FORM_ID,
  SUPPORT_REQUEST_FORM_ID,
} from "Components/Support/supportFormIds";
import { toTelHref } from "Infrastructure/Utils/SupportUtils";
import {
  Resources,
  type ResourceDictionary,
  useResource,
} from "Translations/Resources";
import type { ObjPathProxy } from "ts-object-path";

const RESOURCES = Resources.Support;

type ResourcePath = ObjPathProxy<ResourceDictionary, string>;

/** The two forms keep their own translation namespaces, and the technical report still
    lives under `Resources.Sentry`. */
const FORM_ACTIONS: Record<
  SupportReportType,
  { formId: string; submitLabel: ResourcePath; cancelLabel: ResourcePath }
> = {
  technicalProblem: {
    formId: SENTRY_REPORT_FORM_ID,
    submitLabel: Resources.Sentry.SubmitButtonLabel,
    cancelLabel: Resources.Sentry.CancelButtonLabel,
  },
  supportRequest: {
    formId: SUPPORT_REQUEST_FORM_ID,
    submitLabel: RESOURCES.Request.SubmitButton,
    cancelLabel: RESOURCES.Request.CancelButton,
  },
  contentReport: {
    formId: CONTENT_REPORT_FORM_ID,
    submitLabel: RESOURCES.ContentReport.SubmitButton,
    cancelLabel: RESOURCES.ContentReport.CancelButton,
  },
};

type Props = {
  step: SupportFlowStep;
  reportType: SupportReportType | null;
  isSubmitInFlight: boolean;
  onCancel: () => void;
  onRetry: () => void;
};

/**
 * Whether a step has an action bar at all is the flow's call, not this component's — see
 * SupportFlowDialog. This only decides what the bar contains.
 */
export const SupportFlowFooter = ({
  step,
  reportType,
  isSubmitInFlight,
  onCancel,
  onRetry,
}: Props) => {
  const { t } = useResource();

  if (step === "picker") {
    // Picking a card is what advances the step, so this footer is a note rather than an
    // action bar. Who may see the phone is the flow's call — the dialog renders no footer at
    // all on this step for the queues that don't get it.
    const phoneNumber = t(RESOURCES.Contact.PhoneNumber);

    return (
      <Typography variant="body2" color="text.secondary">
        {t(RESOURCES.Choice.UrgentHelp)}{" "}
        <Link href={toTelHref(phoneNumber)} underline="hover">
          {t(RESOURCES.Choice.CallPhone, { phoneNumber })}
        </Link>
      </Typography>
    );
  }

  if (step === "form") {
    if (!reportType) {
      return null;
    }

    const { formId, submitLabel, cancelLabel } = FORM_ACTIONS[reportType];

    return (
      <Stack direction="row" justifyContent="flex-end" gap={2}>
        <PrimaryButton
          size="small"
          color="info"
          variant="outlined"
          onClick={onCancel}
          disabled={isSubmitInFlight}
        >
          {t(cancelLabel)}
        </PrimaryButton>
        {/* The form element is in the step above this footer, so the button has to reach
            it by id rather than by being nested inside it. */}
        <PrimaryButton
          size="small"
          type="submit"
          form={formId}
          color="primary"
          isLoading={isSubmitInFlight}
          disabled={isSubmitInFlight}
        >
          {t(submitLabel)}
        </PrimaryButton>
      </Stack>
    );
  }

  return (
    <Stack direction="row" justifyContent="flex-end" gap={2}>
      <PrimaryButton
        size="small"
        color="info"
        variant="outlined"
        onClick={onRetry}
      >
        {t(Resources.Common.Retry)}
      </PrimaryButton>
    </Stack>
  );
};
